import { useState } from 'react';
import { Play, Loader, AlertCircle, CheckCircle, RefreshCw, Columns } from 'lucide-react';
import type { QueryFormData } from '../../../types/queryForm';
import type { QueryExecutionResult, AutomationInterval } from '../../../types/query';
import { AUTOMATION_INTERVALS } from '../../../types/query';
import { queryService } from '../../../services/queryService';
import QueryResultViewer from '../QueryResultViewer';

interface Step1DefinitionsProps {
  formData: QueryFormData;
  onChange: (data: Partial<QueryFormData>) => void;
  onColumnsDiscovered: (columns: string[]) => void;
  isEditing: boolean;
  availableColumns: string[];
}

export default function Step1Definitions({
  formData,
  onChange,
  onColumnsDiscovered,
  isEditing,
  availableColumns
}: Step1DefinitionsProps) {
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionResult, setExecutionResult] = useState<QueryExecutionResult | null>(null);

  const handleNameChange = (value: string) => {
    onChange({ name: value });
    if (!isEditing) {
      const slug = queryService.slugify(value);
      onChange({ name: value, endpointSlug: slug });
    }
  };

  const handleExecuteTest = async () => {
    if (!formData.sqlQuery.trim()) return;

    setIsExecuting(true);
    setExecutionResult(null);

    try {
      const result = await queryService.executeRawQuery(formData.sqlQuery);
      setExecutionResult(result);

      if (result.success && result.columns.length > 0) {
        onColumnsDiscovered(result.columns);
        if (!result.columns.includes(formData.columnDistributor)) {
          onChange({ columnDistributor: '' });
        }
        if (!result.columns.includes(formData.columnIndustry)) {
          onChange({ columnIndustry: '' });
        }
      }
    } catch (error) {
      setExecutionResult({
        success: false,
        data: [],
        columns: [],
        rowCount: 0,
        executionTime: 0,
        error: error instanceof Error ? error.message : 'Erro desconhecido'
      });
    } finally {
      setIsExecuting(false);
    }
  };

  const showColumnMapping = availableColumns.length > 0;
  const columnMappingComplete = formData.columnDistributor !== '' && formData.columnIndustry !== '';

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Definições do Serviço</h3>
        <p className="text-sm text-gray-500">Configure o nome, descrição e a consulta SQL que será executada.</p>
      </div>

      <div className="grid grid-cols-1 gap-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nome do Serviço *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleNameChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Ex: Listar usuarios ativos"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Descrição
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => onChange({ description: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            rows={2}
            placeholder="Descricao opcional do servico"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-700">
              Query SQL *
            </label>
            <button
              type="button"
              onClick={handleExecuteTest}
              disabled={isExecuting || !formData.sqlQuery.trim()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors"
            >
              {isExecuting ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              Testar Consulta
            </button>
          </div>
          <textarea
            value={formData.sqlQuery}
            onChange={(e) => onChange({ sqlQuery: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
            rows={8}
            placeholder="SELECT * FROM users WHERE active = true"
          />
        </div>
      </div>

      {executionResult && (
        <div className="border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-2">
            {executionResult.success ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600" />
            )}
            <span className={`font-medium ${
              executionResult.success ? 'text-green-700' : 'text-red-700'
            }`}>
              {executionResult.success
                ? `${executionResult.rowCount} resultado(s) em ${executionResult.executionTime}ms`
                : 'Erro na execucao'
              }
            </span>
          </div>
          <QueryResultViewer result={executionResult} showExport={false} />
        </div>
      )}

      {showColumnMapping && (
        <div className={`border rounded-lg p-4 space-y-4 transition-colors ${
          columnMappingComplete ? 'border-green-200 bg-green-50/30' : 'border-amber-200 bg-amber-50/30'
        }`}>
          <div className="flex items-center gap-2">
            <Columns className={`w-4 h-4 ${columnMappingComplete ? 'text-green-600' : 'text-amber-600'}`} />
            <span className="text-sm font-medium text-gray-800">Mapeamento de Colunas *</span>
            {!columnMappingComplete && (
              <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full">
                Obrigatório
              </span>
            )}
            {columnMappingComplete && (
              <CheckCircle className="w-4 h-4 text-green-600" />
            )}
          </div>
          <p className="text-xs text-gray-500">
            Selecione quais colunas retornadas correspondem ao ID do Distribuidor e ao ID da Indústria.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Coluna IDDistribuidor *
              </label>
              <select
                value={formData.columnDistributor}
                onChange={(e) => onChange({ columnDistributor: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white ${
                  formData.columnDistributor ? 'border-green-300' : 'border-amber-300'
                }`}
              >
                <option value="">Selecione a coluna...</option>
                {availableColumns
                  .filter(col => col !== formData.columnIndustry)
                  .map((col) => (
                    <option key={col} value={col}>{col}</option>
                  ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Coluna IDIndustria *
              </label>
              <select
                value={formData.columnIndustry}
                onChange={(e) => onChange({ columnIndustry: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white ${
                  formData.columnIndustry ? 'border-green-300' : 'border-amber-300'
                }`}
              >
                <option value="">Selecione a coluna...</option>
                {availableColumns
                  .filter(col => col !== formData.columnDistributor)
                  .map((col) => (
                    <option key={col} value={col}>{col}</option>
                  ))}
              </select>
            </div>
          </div>
        </div>
      )}

      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <RefreshCw className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-medium text-gray-700">Periodicidade de Atualização</span>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Frequência de Atualização
          </label>
          <select
            value={formData.automationInterval}
            onChange={(e) => onChange({ automationInterval: e.target.value as AutomationInterval })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            {AUTOMATION_INTERVALS.map((interval) => (
              <option key={interval.value} value={interval.value}>
                {interval.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1.5">
            Define com qual frequência os dados deste serviço serão atualizados automaticamente.
          </p>
        </div>
      </div>
    </div>
  );
}
